import { type GenericMap, type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "@common/lib/consts";
import { formatMoneyDisplay, formatNumber } from "@common/lib/format";
import { isMoney } from "@common/modules/data/lib";
import { type Data } from "@common/modules/data/types";
import Network from "@common/modules/network/types";
import React from "react";
import { getReportBadges } from "../../lib";
import { type BadgeInfo, type GridConfigColumn } from "../../types";
import { type NetworkVolumeAggregate, getYAxis, processAggregates } from "../lib";
import { type ProviderProps } from "../types";

export const NETWORK_VOLUME_REPORT_ID = "E8624472-8A5C-4195-B7E4-6AF300A715DF";

export type NetworkVolumeReportStackBy = "Volume" | "Rank" | "Leg" | "Level" | "BreakawayLevel" | "DifferentialLevel";
export type NetworkVolumeReportConfig = Data.NetworkVolumeConfig & {
  colors?: string[];
  isNoGrid?: boolean;
  reportType?: Data.ReportType;
  stackBy?: NetworkVolumeReportStackBy | null;
};

export type NetworkVolumeReportData = {
  currencyCode: string;
  items: Data.Api.GenealogyCycleAggregate[];
};
export type Props = ProviderProps & {
  config: NetworkVolumeReportConfig;
  cycleOptions: OptionItem<string>[];
  cycleEndDate: string;
  getData: (cycleEndDate: string) => Promise<NetworkVolumeReportData>;
  ranks: Network.Api.RankOptionItem[];
};

const NetworkVolumeReportProvider = ({ config, ...props }: Props) => {
  const i18n = useI18n();

  const fields = React.useMemo(
    (): Data.Api.FormField[] => [
      {
        formControlKey: "Select",
        formFieldId: "cycleEndDate",
        formFieldKey: "cycleEndDate",
        label: i18n.cycle,
        isRequired: true,
        isUnique: false,
        reportId: "",
        sortOrder: 1,
        source: `json:\"${JSON.stringify(props.cycleOptions)}\"`,
        plainTextLabel: i18n.cycle,
        plainTextValue: props.cycleEndDate,
        value: props.cycleEndDate,
        width: 12
      }
    ],
    [i18n, props.cycleEndDate, props.cycleOptions]
  );

  const getBadges = React.useCallback(
    (parameters: GenericMap<any>, filters: Data.Api.Filter[], onBadgeClick: () => void): BadgeInfo[] => {
      return getReportBadges(i18n, {
        badges: ["cycleEndDate"],
        fields: fields,
        parameters: parameters,
        drillDownInfo: EMPTY_ARRAY,
        filters: filters,
        onBadgeClick: onBadgeClick
      });
    },
    [i18n, fields]
  );

  const getChartData = React.useCallback(async (id, params) => {
    const data = await props.getData(params.cycleEndDate);

    return processAggregates(data.items, config, props.ranks, data.currencyCode);
  }, []);

  const getGridData = React.useCallback(async (id, params) => {
    // NOTE: initially fires w/o cycleEndDate. TODO: find out why and fix
    if (!params.cycleEndDate) return [];

    const data = await props.getData(params.cycleEndDate);

    return processAggregates(data.items, config, props.ranks, data.currencyCode);
  }, []);

  return props.children({
    id: props.id,
    config: {
      aspectRatio: 2.5,
      colors: config.colors,
      xAxis: {
        key: config.group.by
      },
      yAxis: {
        get: (data) => getYAxis(i18n, config, props.ranks, data),
        isStacked: !!config.stackBy
      },
      totalRecords: 10,
      yNoData: "hide"
    },
    fields,
    hasGrid: !config.isNoGrid,
    gridConfig: config.isNoGrid
      ? null
      : {
          key: config.group.by,
          columns: [],
          pageSize: 25
        },
    gridColumns: (data) => [
      {
        dataType: "number",
        key: config.group.by,
        label: config.group.by === "rankName" ? i18n.rank : i18n[config.group.by]
      },
      ...getYAxis(i18n, config, props.ranks, data).map<GridConfigColumn<any>>((x) => ({
        dataType: "number",
        key: x.value,
        label: x.label as string,
        data: (y: NetworkVolumeAggregate) =>
          isMoney(config.aggregateBy) ? formatMoneyDisplay(y[x.value], y.currencyCode) : formatNumber(y[x.value])
      })),
      {
        dataType: "number",
        key: "total",
        label: i18n.total,
        data: (y: NetworkVolumeAggregate) =>
          isMoney(config.aggregateBy) ? formatMoneyDisplay(y.total, y.currencyCode) : formatNumber(y.total)
      }
    ],
    isLoading: false,
    name: config.label ?? i18n.networkVolume,
    parameters: EMPTY_OBJECT,
    type: config.reportType ?? "Bar",
    getBadges,
    getChartData,
    getFilters: () =>
      Promise.resolve<Data.Api.Filter[]>([
        {
          displayLabel: null,
          displayName: null,
          key: "cycleEndDate",
          label: i18n.cycleEndDate
        }
      ]),
    getGridData
  });
};
export default NetworkVolumeReportProvider;
